import { useEffect, useMemo, useState } from 'react'
import { Check, Layers, Link2, Search } from 'lucide-react'
import {
  apiListSuppliers,
  type SupplierListItem,
} from '@/lib/api'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Skeleton } from './ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { cn } from '@/lib/utils'

// Story 3.5 — Picker multi-select de fournisseurs catalogue.
// Conçu pour être intégré à la fois dans `CampaignSupplierDialog` (post-création
// campagne) et `CampaignWizard` (étape « Fournisseurs »).
//
// Props :
//   - `excludeCampaignId` : exclut les fournisseurs déjà assignés à cette campagne
//     (CampaignSupplier non soft-deleted). Optionnel — sans, le picker liste tout
//     le catalogue (utilisé par le wizard à la création où la campagne n'existe pas).
//   - `initialSelectedIds` : pré-sélection (utile en retour arrière du wizard).
//   - `onChange(ids)` : appelé à chaque mutation de la sélection.
//   - `emptyCatalogAction` : node optionnel affiché dans l'EmptyState (lien vers
//     la création de fournisseur dans le catalogue — cf. AC #3).

export type CatalogSupplierPickerProps = {
  excludeCampaignId?: string
  initialSelectedIds?: string[]
  onChange?: (selectedIds: string[]) => void
  emptyCatalogAction?: React.ReactNode
  // Plafond MVP — au-delà, on hit serveur pour filtrer (cf. Dev Notes ⚠️
  // pagination). Pour le moment, fetch 1000 max et filtre client.
  pageSize?: number
  // Pour permettre un test d'intégration ciblé du chargement initial.
  testId?: string
}

// Story 3.5 intent gap — badge informatif sur les fournisseurs déjà présents
// dans d'autres campagnes DRAFT/ACTIVE (hors campagne courante via
// `excludeCampaignId` côté backend). Niveau 1 (Marie validée 2026-05-20) :
// signal visible, non bloquant — la sélection reste possible. Tooltip liste
// les noms (max 3 puis « ... »).
function ActiveCampaignsBadge({
  count,
  previews,
}: {
  count: number
  previews: { id: string; name: string }[]
}) {
  const remaining = count - previews.length
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            // role/aria pour rendre l'info accessible en l'absence de tooltip visuel
            role="img"
            aria-label={`Présent dans ${count} campagne${count > 1 ? 's' : ''} en cours`}
            className="inline-flex items-center gap-0.5 rounded-sm bg-amber-100 px-1 py-0 text-[10px] font-medium leading-4 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
            onClick={(e) => e.stopPropagation()}
          >
            <Layers className="h-2.5 w-2.5" aria-hidden />
            +{count}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          <p className="font-medium mb-1">
            Présent dans {count} campagne{count > 1 ? 's' : ''} en cours
          </p>
          {previews.length > 0 && (
            <ul className="space-y-0.5 text-muted-foreground">
              {previews.map((c) => (
                <li key={c.id} className="truncate">· {c.name}</li>
              ))}
              {remaining > 0 && <li>· ... et {remaining} autre{remaining > 1 ? 's' : ''}</li>}
            </ul>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function CatalogSupplierPicker({
  excludeCampaignId,
  initialSelectedIds,
  onChange,
  emptyCatalogAction,
  pageSize = 1000,
  testId,
}: CatalogSupplierPickerProps) {
  const [allSuppliers, setAllSuppliers] = useState<SupplierListItem[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialSelectedIds ?? [])
  )

  // Chargement initial — on n'auto-refetch pas en cas de changement de
  // `excludeCampaignId` car le composant est typiquement monté avec un id stable.
  useEffect(() => {
    let cancelled = false
    setLoadError(null)
    apiListSuppliers({
      pageSize,
      status: 'all',
      sortBy: 'name',
      sortOrder: 'asc',
      ...(excludeCampaignId ? { excludeCampaignId } : {}),
    })
      .then((res) => {
        if (cancelled) return
        setAllSuppliers(res.data)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : 'Erreur lors du chargement du catalogue'
        setLoadError(msg)
        setAllSuppliers([])
      })
    return () => { cancelled = true }
  }, [excludeCampaignId, pageSize])

  // Filtrage client — name | email | company (case-insensitive). Le serveur
  // accepte aussi `search=` mais l'aller-retour est superflu pour ≤1000 lignes.
  const filtered = useMemo(() => {
    if (!allSuppliers) return []
    const q = search.trim().toLowerCase()
    if (!q) return allSuppliers
    return allSuppliers.filter((s) =>
      s.name.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q) ||
      (s.company?.toLowerCase().includes(q) ?? false)
    )
  }, [allSuppliers, search])

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      onChange?.(Array.from(next))
      return next
    })
  }

  function clear() {
    setSelected(new Set())
    onChange?.([])
  }

  const loading = allSuppliers === null
  const catalogEmpty = allSuppliers !== null && allSuppliers.length === 0 && !loadError

  return (
    <div
      data-testid={testId ?? 'catalog-supplier-picker'}
      className="rounded-md border bg-card"
    >
      {/* Barre de recherche */}
      <div className="border-b px-3 py-2 flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
        <Input
          aria-label="Rechercher un fournisseur dans le catalogue"
          placeholder="Rechercher un fournisseur (nom, email, entreprise)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border-0 focus-visible:ring-0 px-0 h-7"
          disabled={loading || catalogEmpty}
        />
        {selected.size > 0 && (
          <button
            type="button"
            onClick={clear}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Effacer
          </button>
        )}
      </div>

      {/* Liste */}
      <div className="max-h-64 overflow-y-auto" role="listbox" aria-multiselectable="true">
        {loading && (
          <div className="p-3 space-y-2">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded" />
            ))}
          </div>
        )}

        {loadError && !loading && (
          <p className="px-3 py-4 text-sm text-destructive" role="alert">
            {loadError}
          </p>
        )}

        {catalogEmpty && (
          <div className="px-3 py-6 text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Votre catalogue de fournisseurs est vide.
            </p>
            {emptyCatalogAction}
          </div>
        )}

        {!loading && !loadError && filtered.length === 0 && allSuppliers && allSuppliers.length > 0 && (
          <p className="px-3 py-4 text-sm text-muted-foreground text-center">
            Aucun fournisseur ne correspond à « {search} »
          </p>
        )}

        {!loading && filtered.length > 0 && (
          <ul className="divide-y">
            {filtered.map((s) => {
              const isSelected = selected.has(s.id)
              return (
                <li
                  key={s.id}
                  role="option"
                  aria-selected={isSelected}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent/50',
                    isSelected && 'bg-accent/40'
                  )}
                  onClick={() => toggle(s.id)}
                >
                  <div
                    className={cn(
                      'h-4 w-4 rounded border flex items-center justify-center flex-shrink-0',
                      isSelected ? 'bg-primary border-primary' : 'border-input'
                    )}
                    aria-hidden
                  >
                    {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate flex items-center gap-1.5">
                      {s.name}
                      <Link2 className="h-3 w-3 text-muted-foreground" aria-label="Fournisseur catalogue" />
                      {s.activeCampaignCount > 0 && (
                        <ActiveCampaignsBadge
                          count={s.activeCampaignCount}
                          previews={s.activeCampaigns}
                        />
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {s.email}
                      {s.company ? ` · ${s.company}` : ''}
                    </p>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* Pied : compteur sélection */}
      {!catalogEmpty && (
        <div className="border-t px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
          <span>
            {selected.size} sélectionné{selected.size > 1 ? 's' : ''}
            {allSuppliers && ` sur ${allSuppliers.length}`}
          </span>
        </div>
      )}
    </div>
  )
}

// ─── Composant bouton « Ajouter N » + picker ──────────────────────────────────
//
// Coquille opinionée pour CampaignSupplierDialog : encapsule le picker et un
// bouton d'action qui appelle le callback `onConfirm` puis vide la sélection.

export type CatalogPickerSectionProps = {
  excludeCampaignId?: string
  busy?: boolean
  onConfirm: (selectedIds: string[]) => Promise<void> | void
  emptyCatalogAction?: React.ReactNode
}

export function CatalogPickerSection({
  excludeCampaignId,
  busy,
  onConfirm,
  emptyCatalogAction,
}: CatalogPickerSectionProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [pickerKey, setPickerKey] = useState(0) // force remount pour vider sélection

  async function handleClick() {
    if (selectedIds.length === 0) return
    await onConfirm(selectedIds)
    setSelectedIds([])
    setPickerKey((k) => k + 1)
  }

  return (
    <div className="space-y-3">
      <CatalogSupplierPicker
        key={pickerKey}
        excludeCampaignId={excludeCampaignId}
        onChange={setSelectedIds}
        emptyCatalogAction={emptyCatalogAction}
      />
      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleClick}
          disabled={selectedIds.length === 0 || busy}
          size="sm"
        >
          {busy
            ? 'Ajout en cours...'
            : selectedIds.length === 0
              ? 'Ajouter les fournisseurs sélectionnés'
              : `Ajouter ${selectedIds.length} fournisseur${selectedIds.length > 1 ? 's' : ''} sélectionné${selectedIds.length > 1 ? 's' : ''}`}
        </Button>
      </div>
    </div>
  )
}
